import React, { useMemo } from 'react';
import { StreamingOverviewResponse } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../../common/types';
import { buildStreamingTiles, FIRST_ROW, SECOND_ROW } from '../streamingTotals';
import { hasPortal } from '../streamingSources';
import { StreamingEarningsTile } from './StreamingEarningsTile';
import { StreamingTotalTile } from './StreamingTotalTile';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StreamingTotalsRowsProps {
  overview: StreamingOverviewResponse;
  /** The sources the reader turned off with the pills. */
  off: string[];
}

/**
 * The totals of direction A "Scoreboard" (design a1): Streams, Listeners, Followers, Subscribers
 * and Net earnings, then the second row of small numbers. A tile with no number while a first
 * read runs is a skeleton (design f5). A member who may not see money gets no Net earnings tile.
 */
const StreamingTotalsRowsComponent: React.FC<StreamingTotalsRowsProps> = ({ overview, off }) => {
  const first = useMemo(() => buildStreamingTiles(overview, FIRST_ROW, off), [off, overview]);
  const second = useMemo(
    () => (hasPortal(overview.sources) ? buildStreamingTiles(overview, SECOND_ROW, off) : []),
    [off, overview]
  );
  const isReading = useMemo(() => overview.sources.some((source) => source.state === 'firstRead'), [overview]);
  const distributorOnly = overview.hasDistributor && !hasPortal(overview.sources);
  const showEarnings = overview.earnings.connected && !overview.earnings.hidden;

  return (
    <>
      <div className={styles.tiles} data-testid="streaming-totals">
        {first.map((tile) =>
          isReading && tile.total.current === null ? (
            <div key={tile.key} className={styles.skeleton} aria-hidden data-testid="streaming-tile-skeleton" />
          ) : (
            <StreamingTotalTile
              key={tile.key}
              tile={tile}
              fromDistributor={overview.hasDistributor}
              distributorOnly={distributorOnly}
            />
          )
        )}
        {showEarnings ? <StreamingEarningsTile earnings={overview.earnings} /> : null}
      </div>
      {second.length > 0 ? (
        <div className={styles.tiles} data-testid="streaming-second-row">
          {second.map((tile) => (
            <StreamingTotalTile
              key={tile.key}
              tile={tile}
              fromDistributor={overview.hasDistributor}
              distributorOnly={distributorOnly}
              small
            />
          ))}
        </div>
      ) : null}
    </>
  );
};

export const StreamingTotalsRows = TypedMemo(StreamingTotalsRowsComponent);
